import { useState, useRef, useEffect, useCallback } from 'react';

export interface UseSpeechToTextOptions {
    lang?: string;
    continuous?: boolean;
    interimResults?: boolean;
    onResult?: (transcript: string, isFinal?: boolean, interimText?: string) => void;
    onError?: (error: any) => void;
    onEnd?: () => void;
}

export const useSpeechToText = (options: UseSpeechToTextOptions = {}) => {
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef<any>(null);
    const isListeningRef = useRef<boolean>(false);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const stopListening = useCallback(() => {
        isListeningRef.current = false;
        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {
                // ignore if already stopped
            }
            recognitionRef.current = null;
        }
        setIsListening(false);
    }, []);

    const startListening = useCallback(() => {
        const SpeechRecognition =
            (window as any).SpeechRecognition ||
            (window as any).webkitSpeechRecognition;

        if (!SpeechRecognition) {
            console.error('Speech recognition not supported in this browser.');
            return;
        }

        // Stop any existing instance cleanly
        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {}
            recognitionRef.current = null;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = optionsRef.current.lang || 'en-US';
        recognition.continuous = optionsRef.current.continuous ?? true;
        recognition.interimResults = optionsRef.current.interimResults ?? true;
        recognition.maxAlternatives = 1;

        let accumulatedFinal = '';

        recognition.onresult = (event: any) => {
            let currentFinal = '';
            let currentInterim = '';

            for (let i = event.resultIndex; i < event.results.length; ++i) {
                const res = event.results[i];
                const text = res[0]?.transcript || '';
                if (res.isFinal) {
                    currentFinal += text;
                } else {
                    currentInterim += text;
                }
            }

            if (currentFinal) {
                accumulatedFinal += (accumulatedFinal && currentFinal ? ' ' : '') + currentFinal.trim();
            }

            const sessionTranscript = (accumulatedFinal + (accumulatedFinal && currentInterim ? ' ' : '') + currentInterim).trim();

            if (optionsRef.current.onResult) {
                optionsRef.current.onResult(sessionTranscript, !!currentFinal, currentInterim);
            }
        };

        recognition.onerror = (event: any) => {
            // Non-fatal errors in continuous mode
            if (event.error === 'no-speech' || event.error === 'aborted') {
                return;
            }
            console.error('Speech recognition error:', event.error);
            if (optionsRef.current.onError) {
                optionsRef.current.onError(event);
            }
        };

        recognition.onend = () => {
            // If user has not explicitly stopped listening, keep listening seamlessly
            if (isListeningRef.current) {
                try {
                    recognition.start();
                    return;
                } catch (e) {
                    // ignore if restart fails
                }
            }
            setIsListening(false);
            if (optionsRef.current.onEnd) {
                optionsRef.current.onEnd();
            }
        };

        try {
            recognition.start();
            recognitionRef.current = recognition;
            isListeningRef.current = true;
            setIsListening(true);
        } catch (err) {
            console.error('Failed to start speech recognition:', err);
            setIsListening(false);
        }
    }, []);

    useEffect(() => {
        return () => {
            stopListening();
        };
    }, [stopListening]);

    return {
        isListening,
        startListening,
        stopListening,
        isSupported: !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)
    };
};
